import { adminAuditApi, type AdminAuditLog } from '@/api/admin/adminAuditApi';
import { ADMIN_JOBS, adminJobsApi } from '@/api/admin/adminJobsApi';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ConfirmDialogAction } from '@/components/admin/ConfirmDialogAction';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { handleGlobalError } from '@/utils/error-handler';
import { logError } from '@/utils/logger';
import { useCallback, useEffect, useState } from 'react';

/**
 * The run line has four states and only one of them shows a date, so it reads
 * better here than as a nested ternary in the JSX.
 */
const describeLastRun = (
  isLoadingHistory: boolean,
  jobFailed: boolean,
  lastRun: AdminAuditLog | undefined,
): string => {
  if (isLoadingHistory) {
    return 'Cargando historial...';
  }
  if (jobFailed) {
    return 'No se pudo cargar el historial';
  }
  if (!lastRun) {
    return 'Sin corridas manuales registradas';
  }
  const when = new Date(lastRun.createdAt).toLocaleString('es-AR');
  return `Última corrida manual: ${when} · ${lastRun.adminEmail} · ${lastRun.result}`;
};

/**
 * Latest manual run of one job, or undefined when it has never been run.
 *
 * One query per job, filtered by targetId. Scanning a single page of RUN_JOB
 * rows instead was wrong: a job whose last run had scrolled past that window
 * read as "Sin corridas manuales registradas", which is a different claim from
 * "not in the last 100 rows" — and the rarely-run jobs, the ones this line
 * matters for, were exactly the ones that fell off it.
 *
 * Newest-first comes from a hardcoded Sort.by(DESC, "createdAt") in
 * AdminAuditFacade.kt, so size: 1 is the latest run.
 */
const fetchLastRun = async (jobId: string): Promise<AdminAuditLog | undefined> => {
  const page = await adminAuditApi.search({
    action: 'RUN_JOB',
    targetId: jobId,
    page: 0,
    size: 1,
  });
  return page.content[0];
};

const AdminJobsPage = () => {
  const { toast } = useToast();
  const [lastRuns, setLastRuns] = useState<Record<string, AdminAuditLog>>({});
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  // Per job, not global: with one request per job the odds that some request
  // fails are as many times higher, and `Promise.all` rejecting on the first of
  // them used to blank all ten history lines over a single timeout.
  const [failedJobIds, setFailedJobIds] = useState<string[]>([]);
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);
  const [runningJobId, setRunningJobId] = useState<string | null>(null);

  // The audit log doubles as the job history: there is no separate runs table.
  const loadLastRuns = useCallback(async () => {
    setIsLoadingHistory(true);
    // allSettled, so the nine answers that did arrive are still shown and only
    // the row that actually failed says so.
    const results = await Promise.allSettled(
      ADMIN_JOBS.map((job) => fetchLastRun(job.id)),
    );

    const byJob: Record<string, AdminAuditLog> = {};
    const failed: string[] = [];
    results.forEach((result, index) => {
      const jobId = ADMIN_JOBS[index].id;
      if (result.status === 'rejected') {
        // A missing history must not hide the run buttons, so this stays silent
        // in the UI beyond that job's own line — but it still belongs in the
        // console.
        logError(`Failed to load the run history of ${jobId}`, result.reason);
        failed.push(jobId);
        return;
      }
      if (result.value) {
        byJob[jobId] = result.value;
      }
    });

    setLastRuns(byJob);
    setFailedJobIds(failed);
    setIsLoadingHistory(false);
  }, []);

  // After running one job that is the only row that moved, so the other nine
  // requests would be pure noise — and nine more chances to fail.
  const refreshLastRun = useCallback(async (jobId: string) => {
    try {
      const lastRun = await fetchLastRun(jobId);
      if (lastRun) {
        setLastRuns((current) => ({ ...current, [jobId]: lastRun }));
      }
      setFailedJobIds((current) => current.filter((id) => id !== jobId));
    } catch (error) {
      logError(`Failed to refresh the run history of ${jobId}`, error);
      setFailedJobIds((current) =>
        current.includes(jobId) ? current : [...current, jobId],
      );
    }
  }, []);

  useEffect(() => {
    loadLastRuns();
  }, [loadLastRuns]);

  const runJob = async () => {
    if (!pendingJobId) return;
    const jobId = pendingJobId;
    setRunningJobId(jobId);
    try {
      await adminJobsApi.run(jobId);
      toast({ title: 'Job ejecutado' });
    } catch (error) {
      handleGlobalError(error, 'El job falló', 'Revisá la auditoría para ver el detalle.');
    } finally {
      // Both paths: a job that failed still wrote its RUN_JOB row, and that row
      // is the detail the error toast points at.
      await refreshLastRun(jobId);
      setRunningJobId(null);
      setPendingJobId(null);
    }
  };

  const pendingJob = ADMIN_JOBS.find((job) => job.id === pendingJobId);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Jobs</h1>
        <p className="text-sm text-gray-500">
          Solo se listan las corridas manuales: los jobs también corren solos por
          schedule, y esas no quedan registradas. Informan si corrieron o si
          fallaron, pero no cuántos registros procesaron.
        </p>
      </div>

      <div className="space-y-2">
        {ADMIN_JOBS.map((job) => {
          const lastRun = lastRuns[job.id];
          return (
            <Card key={job.id} className="flex items-center justify-between p-4">
              <div className="pr-4">
                <h2 className="font-medium text-gray-900">{job.label}</h2>
                <p className="text-sm text-gray-500">{job.description}</p>
                <p className="mt-1 text-xs text-gray-400">
                  {describeLastRun(
                    isLoadingHistory,
                    failedJobIds.includes(job.id),
                    lastRun,
                  )}
                </p>
              </div>
              <Button
                variant="outline"
                disabled={runningJobId === job.id}
                onClick={() => setPendingJobId(job.id)}
              >
                {runningJobId === job.id ? 'Ejecutando...' : 'Ejecutar'}
              </Button>
            </Card>
          );
        })}
      </div>

      <AlertDialog
        open={pendingJobId !== null}
        onOpenChange={(open) => !open && setPendingJobId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ejecutar {pendingJob?.label}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingJob?.description}{' '}
              {pendingJob?.warning ??
                'Se ejecuta ahora sobre datos reales y puede enviar notificaciones o emails.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={runningJobId !== null}>Cancelar</AlertDialogCancel>
            <ConfirmDialogAction
              onConfirm={runJob}
              isRunning={runningJobId !== null}
              runningLabel="Ejecutando..."
            >
              Ejecutar
            </ConfirmDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminJobsPage;
